import humps from 'humps'

import { get, patch, post, del, blobInstance } from './requests'
import { getToken } from '_/utils/token'

export const getAgencies = key => params =>
  get(['agencyboard'], { key, params: humps.decamelizeKeys(params), transformPayload: true })

export const getAgency = key => (agencyId, params) =>
  get(['agency', agencyId], { key, params, transformPayload: true })

export const getAgencyFull = key => (agencyId, params) =>
  get(['agency', agencyId, 'full-agency-detail'], { key, params, transformPayload: true })

export const getAgenciesSimple = key => params =>
  get(['simple-agency'], { key, transformPayload: true, params })

export const updateAgency = key => (agencyId, payload) => {
  const shouldTransformIntoFormData =
    payload?.brandLogoPngUrl instanceof Blob ||
    payload?.faviconUrl instanceof Blob ||
    payload?.budgetCoverPicture instanceof File

  return patch(
    ['agency', agencyId],
    {
      key,
      transformPayload: true,
      transformFormData: shouldTransformIntoFormData,
      multipartArraysAsJson: shouldTransformIntoFormData,
      formData: shouldTransformIntoFormData,
    },
    payload
  )
}
export const createAgency = key => payload => {
  const shouldTransformIntoFormData =
    payload?.brandLogoPngUrl instanceof Blob || payload?.faviconUrl instanceof Blob
  return post(
    ['agency'],
    {
      key,
      transformPayload: true,
      transformFormData: shouldTransformIntoFormData,
      formData: true,
    },
    payload
  )
}

export const getAgencyStaffUsers = (agencyId, userId = undefined, params = {}) => {
  const headers = getToken()
  const urlFragmentList = userId
    ? ['agency', agencyId, 'staff-users', userId]
    : ['agency', agencyId, 'staff-users']

  return get(urlFragmentList, { ...headers, transformPayload: true, params })
}

export const getAccountManagers = key => agencyId =>
  get(['agency', agencyId, 'staff-users', 'get-agency-account-managers'], {
    key,
  })

export const addAgencyStaffUser = key => (agencyId, payload) =>
  post(['agency', agencyId, 'staff-users'], { key, transformPayload: true }, payload)

export const deleteAgencyStaffUser = key => (agencyId, staffId) =>
  del(['agency', agencyId, 'staff-users', staffId], { key, transformPayload: true })

export const inactivateAgencyStaffUser = key => (agencyId, staffId) =>
  post(
    ['agency', agencyId, 'staff-users', staffId, 'inactivate-user'],
    { key, transformPayload: true },
    {}
  )

export const deleteAgencyContract = key => (agencyId, attachmentId) =>
  patch(['agency', agencyId, 'delete-contract'], { key }, { attachmentId })

export const updateAgencyStaffUser = key => (agencyId, staffId, payload) =>
  patch(['agency', agencyId, 'staff-users', staffId], { key, transformPayload: true }, payload)

export const getServiceOrderResponsible =
  key =>
  (agencyId, responsibleId = null) =>
    get(['agency', agencyId, 'responsible-options'], {
      key,
      params: { responsible_id: responsibleId },
    })

export const getOptionsFieldName = key => agencyId =>
  get(['options-field-name'], { key, transformPayload: true, params: { agency: agencyId } })

export const getSectorsAgencyByUserId = key => userId =>
  get(['agency', userId, 'find-sectors-agency'], { key, transformPayload: true })

export const getPages = key => agencyId =>
  get(['form-pages'], { key, transformPayload: true, params: { agency_id: agencyId } })

export const getAdditionalTakeRatesByAgency = key => agencyId =>
  get(['additional-take-rates'], { key, transformPayload: true, params: { agency_id: agencyId } })

export const getAgencyDetails = key => agencyId =>
  get(['agency', agencyId, 'agency-details'], { key })

export const downloadReport = () => {
  const headers = getToken()
  return get(['agency', 'download-report'], { ...headers })
}

export const getMacroStepsService = key => params => {
  return get(['macro-steps'], { key, transformPayload: true, params })
}

export const getMacroStepsServiceCustom = key => params => {
  return get(['macro-steps-custom'], { key, transformPayload: true, params })
}

export const getMacroStepByIdService = key => macroStepId =>
  get(['macro-steps', macroStepId], { key })

export const patchMacroStepToAgencyService = key => (id, payload) => {
  return patch(
    ['macro-steps-custom', id],
    {
      key,
    },
    payload
  )
}

export const postMacroStepToAgencyService = key => payload => {
  return post(
    ['macro-steps-custom'],
    {
      key,
    },
    payload
  )
}

export const getAgencyAdditionalConfig = key => agencyId =>
  get(['agency', agencyId, 'additional-config'], { key, transformPayload: true })

export const createAgencyAdditionalConfig = key => (agencyId, payload) =>
  post(
    ['agency', agencyId, 'additional-config'],
    { key, transformPayload: true, transformFormData: true, formData: true },
    payload
  )

export const updateAgencyAdditionalConfig = key => (agencyId, payload) =>
  patch(
    ['agency', agencyId, 'additional-config'],
    { key, transformPayload: false, formData: true },
    payload
  )

export const getBuildings = key => agencyId => {
  return get(['agency', agencyId, 'buildings'], { key, transformPayload: true })
}

export const getActiveBuildingsByAgency = key => agencyId => {
  return get(['agency', agencyId, 'buildings', 'active'], { key, transformPayload: true })
}

export const getBuildingTypesService = key => params =>
  get(['building-types'], { key, transformPayload: true, params })

export const getBuildingsListByUser = key => params =>
  get(['buildings-list-by-user'], {
    key,
    transformPayload: true,
    params,
  })

export const getAgencyBuildingById = key => (agencyId, buildingId) =>
  get(['agency', agencyId, 'buildings', buildingId], { key, transformPayload: true })

export const patchAgencyBuildingService = key => (id, agencyId, payload) => {
  return patch(['agency', agencyId, 'buildings', id], { key, transformPayload: true }, payload)
}

export const postAgencyBuildingService = key => (agencyId, payload) => {
  return post(['agency', agencyId, 'buildings'], { key, transformPayload: true }, payload)
}

export const patchBuildingUnitService = key => (agencyId, buildingId, id, payload) => {
  return patch(
    ['agency', agencyId, 'buildings', buildingId, 'units', id],
    { key, transformPayload: true },
    payload
  )
}

export const postBuildingUnitService = key => (agencyId, buildingId, payload) => {
  return post(
    ['agency', agencyId, 'buildings', buildingId, 'units'],
    { key, transformPayload: true },
    payload
  )
}

export const getBuildingUnitsByIdService = key => (agencyId, buildingId) =>
  get(['agency', agencyId, 'buildings', buildingId, 'units'], { key, transformPayload: true })

export const downloadFilteredReports = async queryParams => {
  const headers = getToken()
  return blobInstance.get(`/agency/download-report-filter/?${queryParams}`, {
    ...headers,
    'Content-Type': 'application/json',
  })
}

export const getAgencyWebhookById = key => (webhookId, params) =>
  get(['webhook', webhookId], {
    key,
    transformPayload: false,
    params,
  }).then(data => {
    const { json_definition: jsonDefinition, webhook_json: webhookJson, ...rest } = data

    return {
      ...humps.camelizeKeys(rest),
      jsonDefinition,
      webhookJson,
    }
  })

export const getWebhooks = key => params => {
  return get(['webhook'], { key, transformPayload: true, params })
}

export const patchWebhookService = key => (id, payload) => {
  return patch(['webhook', id], { key, transformPayload: true }, payload)
}

export const getWebhookQueue = key => (webhookId, params) => {
  return get(['webhook-queue', webhookId], { key, transformPayload: false, params })
}

// Kanban services

export const getKanbanConfig = key => (agencyId, params) => {
  return get(['agency', agencyId, 'kanban-config'], { key, transformPayload: true, params })
}

export const createKanbanConfig = key => (agencyId, payload) => {
  return post(['agency', agencyId, 'kanban-config'], { key, transformPayload: true }, payload)
}

export const updateKanbanConfig = key => (agencyId, payload) => {
  return patch(['agency', agencyId, 'kanban-config'], { key, transformPayload: true }, payload)
}

export const createMessageChannel = key => (agencyId, payload) => {
  return post(['agency', agencyId, 'message-channels'], { key, transformPayload: true }, payload)
}

export const connectMessageChannel = key => (agencyId, messageChannelId) => {
  return post(['agency', agencyId, 'message-channels', messageChannelId, 'connect'], {
    key,
    transformPayload: true,
  })
}

export const getMessageChannelQrCode = key => (agencyId, messageChannelId) =>
  get(['agency', agencyId, 'message-channels', messageChannelId, 'qrcode'], {
    key,
    transformPayload: true,
  })

export const getMessageChannelStatus = key => (agencyId, messageChannelId) =>
  get(['agency', agencyId, 'message-channels', messageChannelId, 'status'], {
    key,
    transformPayload: true,
  })

export const testMessageChannelConnection = key => (agencyId, messageChannelId, payload) =>
  post(
    ['agency', agencyId, 'message-channels', messageChannelId, 'test'],
    {
      key,
      transformPayload: true,
    },
    payload
  )

export const disconnectMessageChannel = key => (agencyId, messageChannelId) =>
  post(['agency', agencyId, 'message-channels', messageChannelId, 'disconnect'], {
    key,
    transformPayload: true,
  })

export const getMessageChannels = key => agencyId =>
  get(['agency', agencyId, 'message-channels'], { key, transformPayload: true })
